import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Who We Are : option cadeau par bijou dans le panier (remplace l'app Wrapped).
 *
 * Chaque bijou peut avoir une ligne « Option cadeau » liée par la propriété cachée `_gift_for`
 * (= key de la ligne du bijou). Après chaque changement, on diffuse un `CartLinesUpdateEvent`
 * avec les sections du panier : `cart-items-component` (tiroir et page panier) les réutilise
 * pour se re-rendre, exactement comme après un ajout depuis la fiche produit.
 */

const GIFT_FOR_PROPERTY = '_gift_for';
const MESSAGE_MAX_LENGTH = 250;

/** @returns {string[]} */
function getCartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/**
 * @param {string} url
 * @param {object} body
 */
async function postCart(url, body) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Cart request failed');
  }
  return data;
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Removes and/or adds gift lines, then broadcasts the change so every cart view re-renders.
 * @param {EventTarget} target - Element (or document) that dispatches the cart event
 * @param {{ remove?: string[], add?: { id: number, properties: Record<string, string> } | null }} change
 */
async function applyGiftChange(target, { remove = [], add = null }) {
  const sectionIds = getCartSectionIds().join(',');
  const sectionsUrl = window.location.pathname;
  const deferred = CartLinesUpdateEvent.createPromise();

  /** @type {any[]} */
  const lines = remove.map((id) => ({ id, quantity: 0 }));
  if (add) lines.push({ merchandiseId: String(add.id), quantity: 1 });

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: add ? 'update' : 'remove',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    /** @type {Record<string, string> | undefined} */
    let sections;

    if (remove.length > 0) {
      /** @type {Record<string, any>} */
      const body = { updates: Object.fromEntries(remove.map((key) => [key, 0])) };
      if (!add) {
        body.sections = sectionIds;
        body.sections_url = sectionsUrl;
      }
      const data = await postCart(`${Theme.routes.cart_update_url}.js`, body);
      if (!add) sections = data.sections;
    }

    if (add) {
      const data = await postCart(Theme.routes.cart_add_url, {
        items: [{ id: add.id, quantity: 1, properties: add.properties }],
        sections: sectionIds,
        sections_url: sectionsUrl,
      });
      sections = data.sections;
    }

    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'gift-option-component',
        didError: false,
      },
    });

    return cart;
  } catch (error) {
    deferred.reject(error);
    target.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Gift option update failed',
        code: 'INVALID',
      })
    );
    throw error;
  }
}

/**
 * Gift option attached to one jewel line.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [toggle]
 * @property {HTMLElement} [panel]
 * @property {HTMLSelectElement} [variantSelect]
 * @property {HTMLTextAreaElement} [message]
 * @property {HTMLElement} [count]
 * @property {HTMLButtonElement} [saveButton]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
class GiftOptionComponent extends Component {
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#syncExpanded();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncExpanded();
  }

  get lineKey() {
    return this.dataset.lineKey ?? '';
  }

  get giftLineKey() {
    return this.dataset.giftLineKey ?? '';
  }

  #syncExpanded() {
    const { toggle, panel } = this.refs;
    if (toggle && panel) toggle.setAttribute('aria-controls', panel.id || '');
    toggle?.setAttribute('aria-expanded', String(Boolean(toggle?.checked)));
  }

  /** @param {Event} _event */
  onToggle(_event) {
    const { toggle, panel, variantSelect } = this.refs;
    if (!toggle || !panel) return;

    const checked = toggle.checked;
    toggle.setAttribute('aria-expanded', String(checked));
    panel.hidden = !checked;

    if (checked) {
      variantSelect?.focus();
      return;
    }

    // Décoché : on retire la ligne « Option cadeau » liée, s'il y en a une
    if (this.giftLineKey) {
      this.#run({ remove: [this.giftLineKey] });
    } else {
      this.#setStatus('');
    }
  }

  /** @param {Event} _event */
  onMessageInput(_event) {
    const { message, count } = this.refs;
    if (!message || !count) return;
    count.textContent = `${message.value.length}/${MESSAGE_MAX_LENGTH} caractères`;
  }

  /** @param {Event} _event */
  save(_event) {
    const { variantSelect, message } = this.refs;
    const variantId = Number(variantSelect?.value);
    if (!variantId || !this.lineKey) return;

    const text = (message?.value ?? '').trim().slice(0, MESSAGE_MAX_LENGTH);

    /** @type {Record<string, string>} */
    const properties = { Pour: this.dataset.giftFor ?? '' };
    if (text) properties['Petit mot'] = text;
    properties[GIFT_FOR_PROPERTY] = this.lineKey;

    this.#run({
      remove: this.giftLineKey ? [this.giftLineKey] : [],
      add: { id: variantId, properties },
    });
  }

  /**
   * @param {{ remove?: string[], add?: { id: number, properties: Record<string, string> } | null }} change
   */
  async #run(change) {
    if (this.#busy) return;
    this.#busy = true;

    const { saveButton } = this.refs;
    saveButton?.setAttribute('aria-busy', 'true');
    if (saveButton) saveButton.disabled = true;
    this.#setStatus('Un instant...');

    try {
      await applyGiftChange(this, change);
      // The cart section re-renders this component with the saved state.
      this.#setStatus(change.add ? 'Option cadeau enregistrée.' : '');
    } catch (error) {
      console.error('[gift-option]', error);
      this.#setStatus("Oups, l'option cadeau n'a pas pu être enregistrée. Réessayez dans un instant.");
    } finally {
      this.#busy = false;
      const button = this.refs.saveButton;
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  }

  /** @param {string} text */
  #setStatus(text) {
    if (this.refs.status) this.refs.status.textContent = text;
  }
}

if (!customElements.get('gift-option-component')) {
  customElements.define('gift-option-component', GiftOptionComponent);
}

/*
 * Nettoyage : si un bijou est retiré du panier, sa ligne « Option cadeau » est retirée aussi.
 */
const attemptedOrphanKeys = new Set();
let orphanCleanupRunning = false;

/** @param {Array<{ key: string, properties?: Record<string, string> | null }>} items */
async function removeOrphanGiftLines(items) {
  if (orphanCleanupRunning) return;

  const keys = new Set(items.map((item) => item.key));
  const orphans = items
    .filter((item) => {
      const parentKey = item.properties?.[GIFT_FOR_PROPERTY];
      return parentKey && !keys.has(parentKey) && !attemptedOrphanKeys.has(item.key);
    })
    .map((item) => item.key);

  if (orphans.length === 0) return;

  orphanCleanupRunning = true;
  for (const key of orphans) attemptedOrphanKeys.add(key);

  try {
    await applyGiftChange(document, { remove: orphans });
  } catch (error) {
    console.error('[gift-option] orphan cleanup failed', error);
  } finally {
    orphanCleanupRunning = false;
  }
}

/** @param {any} detail */
async function getCartItems(detail) {
  const items = detail?.items;
  if (Array.isArray(items) && items.every((item) => item && 'key' in item && 'properties' in item)) return items;
  const cart = await fetchCart();
  return cart.items;
}

if (!window.__wwaGiftCleanupRegistered) {
  window.__wwaGiftCleanupRegistered = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const cartEvent = /** @type {any} */ (event);
    if (!cartEvent.promise) return;

    cartEvent.promise
      .then(async (/** @type {any} */ result) => {
        if (result?.detail?.didError) return;
        // Our own cleanup already ran on this cart state.
        if (result?.detail?.source === 'gift-option-component' && cartEvent.action === 'remove') return;
        const items = await getCartItems(result?.detail);
        await removeOrphanGiftLines(items);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[gift-option] cart update not handled', error);
      });
  });

  // Une ligne cadeau orpheline déjà présente au chargement (marquée par Liquid)
  if (document.querySelector('[data-gift-orphan]')) {
    fetchCart()
      .then((cart) => removeOrphanGiftLines(cart.items))
      .catch((error) => console.warn('[gift-option]', error));
  }
}
